"use client";

import React, { useState } from "react";
import { CTAButton } from "fansunited-sports-ui";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { MatchList } from "@/components/matches";
import { mapMatchToMatchListItem } from "@/utilities/mappers/match";
import { useTeamLogoUrl } from "@/components/ui/TeamLogo";
import type { MatchRow } from "@/utilities/data/competition/derive/types";

export interface MatchTabbedListProps {
    finishedMatches: MatchRow[];
    upcomingMatches: MatchRow[];
}

type TabType = "upcoming" | "results";

/**
 * MatchTabbedList - Matches section with tabbed view using pills.
 * Shows Upcoming Matches (including live) by default, with Past Matches in second tab.
 */
export const MatchTabbedList: React.FC<MatchTabbedListProps> = ({ finishedMatches, upcomingMatches }) => {
    const labels = useLabels();
    const nav = useNavigation();
    const resolveLogoUrl = useTeamLogoUrl();
    const [activeTab, setActiveTab] = useState<TabType>("upcoming");

    const hasUpcoming = upcomingMatches.length > 0;
    const hasResults = finishedMatches.length > 0;

    // Don't render if no matches at all
    if (!hasUpcoming && !hasResults) {
        return null;
    }

    return (
        <section>
            {/* Header with Pills Tabs and CTA */}
            <div className="flex items-center justify-between mb-4">
                {/* Pills Tabs */}
                <div className="flex gap-2">
                    <button
                        onClick={() => setActiveTab("upcoming")}
                        className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer ${
                            activeTab === "upcoming"
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                        }`}
                    >
                        <span className="sm:hidden">{labels.homePage.upcomingMatchesMobile}</span>
                        <span className="hidden sm:inline">{labels.homePage.upcomingMatches}</span>
                    </button>
                    <button
                        onClick={() => setActiveTab("results")}
                        className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer ${
                            activeTab === "results"
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                        }`}
                    >
                        <span className="sm:hidden">{labels.homePage.latestResultsMobile}</span>
                        <span className="hidden sm:inline">{labels.homePage.latestResults}</span>
                    </button>
                </div>

                {/* CTA Button */}
                <CTAButton
                    text={labels.homePage.allFixtures}
                    href={nav.matchesList()}
                    variant="custom"
                    styles={{
                        container:
                            "text-sm font-medium text-accent hover:text-accent/80 border-none p-0 hidden sm:block",
                    }}
                />
                <CTAButton
                    text={labels.homePage.allFixturesMobile}
                    href={nav.matchesList()}
                    variant="custom"
                    styles={{
                        container: "text-sm font-medium text-accent hover:text-accent/80 border-none p-0 sm:hidden",
                    }}
                />
            </div>

            {/* Tab Content */}
            {activeTab === "upcoming" && (
                <MatchList
                    matches={upcomingMatches.map((match) =>
                        mapMatchToMatchListItem(match, false, false, nav.match, resolveLogoUrl)
                    )}
                    variant="teamFixtures"
                    emptyMessage={labels.homePage.noUpcomingMatches}
                />
            )}

            {activeTab === "results" && (
                <MatchList
                    matches={finishedMatches.map((match) =>
                        mapMatchToMatchListItem(match, true, false, nav.match, resolveLogoUrl)
                    )}
                    variant="teamResults"
                    emptyMessage={labels.homePage.noRecentResults}
                />
            )}
        </section>
    );
};
